/**
 * CSVの生成。
 *
 * ADM-10。値に含まれる引用符・改行・カンマを壊さずに出す。
 * 以前は `"` を `\"` に置換していたため、CSVとしては引用の終端になり、
 * 列がずれていた。CSVの規約では `"` は `""` で表す。
 *
 * ADM-10。`=` `+` `-` `@` `\t` `\r` で始まる値は、表計算ソフトが数式として
 * 解釈する。値の先頭に `'` を付けて文字列として読ませる。
 */

import {
  businessUtcOffsetMinutes,
  compactUtcOffset,
  formatUtcOffset,
  getBusinessTimeZone,
  isoToBusinessClock,
  toBusinessDate,
} from './time';

const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** 1つの値をCSVのフィールドにする。 */
export function toCsvField(value: unknown): string {
  if (value === null || value === undefined) return '""';
  let text = String(value);
  if (FORMULA_PREFIXES.some((prefix) => text.startsWith(prefix))) {
    text = `'${text}`;
  }
  return `"${text.replace(/"/g, '""')}"`;
}

/** 行の配列をCSV本文にする。改行はCRLF。 */
export function toCsv(headers: string[], rows: unknown[][]): string {
  const lines = [headers.map(toCsvField).join(',')];
  rows.forEach((row) => lines.push(row.map(toCsvField).join(',')));
  return lines.join('\r\n');
}

/**
 * CSVをダウンロードさせる。
 *
 * ADM-11。ExcelがUTF-8と判断できるようBOMを付ける。付けないと日本語が
 * 文字化けし、利用者は「出力が壊れている」と受け取る。
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** CSV に出した条件。[名前, 値]。値が空のものはファイル名に入れない。 */
export type CsvCondition = [string, string | number | boolean | null | undefined];

// ファイル名に使えない文字と空白。日本語はそのまま使う。
const UNSAFE_FILENAME_CHARS = /[\s/\\:*?"<>|]/g;

/**
 * CSV のファイル名。ADM-11-AC2。
 *
 * 以前はどの画面も export.csv で、出力した条件・件数・作成日時が分からなかった。
 * 本文の列（帳票の形）は変えずに、`名前_条件-値_…_件数rows_作成日時+0900.csv` にする。
 * 作成日時は業務時間帯（会社の時間帯）。
 *
 * EV-116 段階2（2026-10-11 PM 決定6）。作成日時の後ろは、作成した時点の業務時間帯の UTC との差
 * （`+0900`・`-0700`）。以前は文字 `JST` だった。略称は夏時間で変わり、名前も重なるので使わない。
 */
export function csvFilename(
  name: string,
  conditions: CsvCondition[],
  rows: number,
  now: Date = new Date()
): string {
  const parts: string[] = [];
  conditions.forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    parts.push(`${key}-${String(value)}`.replace(UNSAFE_FILENAME_CHARS, '_'));
  });
  const offset = compactUtcOffset(businessUtcOffsetMinutes(now));
  const stamp = `${toBusinessDate(now).replace(/-/g, '')}-${isoToBusinessClock(now.toISOString()).replace(/:/g, '')}${offset}`;
  const tail = `${rows}rows_${stamp}.csv`;
  const condition = parts.join('_');
  const full = [name, condition, tail].filter(Boolean).join('_');
  if (utf8Bytes(full) <= MAX_FILENAME_BYTES) return full;

  // RV-122。条件が長いと 255 バイトを超える（長い検索語・多数の配送先・日本語は1文字3バイト）。
  // 名前・件数・作成日時・.csv は必ず残し、条件だけを切る。切ったときは、条件の全体から
  // 作った短いハッシュを付け、条件が違えば別の名前になるようにする。
  const hash = `~${fnv1a(condition)}`;
  const budget = MAX_FILENAME_BYTES - utf8Bytes(`${name}__${tail}`) - utf8Bytes(hash);
  const cut = truncateUtf8(condition, Math.max(budget, 0));
  return `${name}_${cut}${hash}_${tail}`;
}

/** ファイル名の上限（多くのファイルシステムで 255 バイト）。 */
const MAX_FILENAME_BYTES = 255;

/** UTF-8 のバイト数。 */
function utf8Bytes(text: string): number {
  let total = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    total += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return total;
}

/** UTF-8 で maxBytes 以内に切る。文字（コードポイント）の途中では切らない。 */
function truncateUtf8(text: string, maxBytes: number): string {
  let used = 0;
  let out = '';
  for (const char of text) {
    const size = utf8Bytes(char);
    if (used + size > maxBytes) break;
    used += size;
    out += char;
  }
  return out;
}

/** 短い安定したハッシュ（FNV-1a 32bit、16進8桁）。 */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    hash ^= code;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** CSV の日時。UTC の ISO 文字列を業務時間帯の `YYYY-MM-DD HH:mm:ss` にする。空・不正は空文字。 */
export function csvDateTime(value: string | null | undefined): string {
  const date = toBusinessDate(value);
  return date ? `${date} ${isoToBusinessClock(value)}` : '';
}

/** 行ごとの UTC との差の列の見出し（EV-116 段階2。今の CSV の見出しと同じく英語）。 */
export const CSV_UTC_OFFSET_HEADER = 'UTC Offset';

/**
 * 1行に日時の列が2つ以上あるときの、列ごとの差の列の見出し（RV-276）。開始と停止で差が違う行
 * （作業そのものが夏時間をまたぐ）でも、どちらの時刻がどの差かが分かるようにする。
 */
export const csvUtcOffsetHeader = (column: string) => `${column} UTC Offset`;

/**
 * 業務時間帯で計算した日付・時刻の列の見出しに付ける時間帯（EV-116 段階2、2026-10-11 PM 決定6）。
 *
 * 出力する値（UTC の時刻）の業務時間帯の UTC との差が1つなら `UTC+09:00`。値が無ければ作成した時点の差。
 * 夏時間をまたいで差が2つ以上あれば、時間帯の名前（`America/Los_Angeles`）を返し、perRow を true にする
 * （呼び出し元は行ごとの差の列 CSV_UTC_OFFSET_HEADER を足す）。
 */
export function csvZoneLabel(
  values: (string | null | undefined)[],
  now: Date = new Date()
): { label: string; perRow: boolean } {
  const offsets = new Set<number>();
  values.forEach((value) => {
    if (toBusinessDate(value)) offsets.add(businessUtcOffsetMinutes(value));
  });
  if (offsets.size > 1) return { label: getBusinessTimeZone(), perRow: true };
  const [offset] = offsets.size === 1 ? Array.from(offsets) : [businessUtcOffsetMinutes(now)];
  return { label: formatUtcOffset(offset), perRow: false };
}

/** その時刻の業務時間帯の UTC との差（`UTC-08:00`）。行ごとの差の列の値。空・不正は空文字。 */
export function csvUtcOffset(value: string | null | undefined): string {
  return toBusinessDate(value) ? formatUtcOffset(businessUtcOffsetMinutes(value)) : '';
}

/**
 * 記録ごとの端末の時間帯の列（EV-116 段階3、2026-10-11 PM の判断）。見出しは今の CSV と同じく英語。
 * 端末の時間帯が無い記録（古いアプリ）は空欄にする。推定の値（会社の時間帯など）は出さない。
 */
export const CSV_DEVICE_TIME_ZONE_HEADER = 'Device Time Zone';
export const CSV_DEVICE_UTC_OFFSET_HEADER = 'Device UTC Offset';

/** 端末の時間帯の名前の列の値。無ければ空文字。 */
export function csvDeviceTimeZone(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

/** 端末の UTC との差の列の値（`UTC-07:00`）。無ければ空文字。 */
export function csvDeviceUtcOffset(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatUtcOffset(value) : '';
}

/**
 * RV-325（2026-10-11 PM 承認の案 B）。測定を1行に束ねる CSV（最新の報告状況）の端末の時間帯の列。
 * 区切り（「; 」）と、無い値を空のまま位置を保つ決まりは EV-141 の Test IDs と同じ。値の書き方は
 * 1行1測定の列（csvDeviceTimeZone・csvDeviceUtcOffset）と同じ。
 */
export const CSV_DEVICE_TIME_ZONES_HEADER = 'Device Time Zones';
export const CSV_DEVICE_UTC_OFFSETS_HEADER = 'Device UTC Offsets';

export function bundledDeviceTimeZoneExportFields(field: string) {
  const join = (format: (value: any) => string, key: string) => (records: any[]) =>
    Array.isArray(records) ? records.map((record) => format(record?.[key])).join('; ') : '';
  return [
    { header: CSV_DEVICE_TIME_ZONES_HEADER, field, customExport: join(csvDeviceTimeZone, 'device_time_zone') },
    { header: CSV_DEVICE_UTC_OFFSETS_HEADER, field, customExport: join(csvDeviceUtcOffset, 'device_utc_offset_minutes') },
  ];
}

/** Export の列の定義（prefix は勤怠の退勤 `check_out_`・作業の停止 `stop_`。見出しの前置きは headerPrefix）。 */
export function deviceTimeZoneExportFields(prefix = '', headerPrefix = '') {
  return [
    { header: `${headerPrefix}${CSV_DEVICE_TIME_ZONE_HEADER}`, field: `${prefix}device_time_zone`, customExport: csvDeviceTimeZone },
    { header: `${headerPrefix}${CSV_DEVICE_UTC_OFFSET_HEADER}`, field: `${prefix}device_utc_offset_minutes`, customExport: csvDeviceUtcOffset },
  ];
}
